/**
 * One table plus its chairs.
 *
 * Chair positions come straight from the shared geometry module every render —
 * they are never stored, never incrementally adjusted, and never touched by the
 * drag code. Dragging moves the table's centre only; the chairs follow because
 * they are derived from it. That is the whole "chairs are stable" guarantee.
 */

import { motion, useReducedMotion } from 'framer-motion'
import { memo, useMemo, useRef } from 'react'

import { tablesCollide } from '../lib/collision'
import { screenSwatchFor } from '../lib/colors'
import {
  SEAT_PLATE_REACH,
  SEAT_RADIUS,
  computeTableGeometry,
  seatWorldPositions,
} from '../lib/geometry'
import { useT } from '../lib/i18n/I18nProvider'
import { finishPlacing } from '../lib/placing'
import type { Guest, SeatLabelMode, Table } from '../lib/types'
import { usePlanStore } from '../store/planStore'
import { snap, tapsSuppressed, useViewStore } from '../store/viewStore'
import { SeatNode } from './SeatNode'

/*
 * Memoised, with props that only change when this table does: a big wedding is
 * 120 of these and 1,224 chairs, and panning, zooming or dragging one table must
 * not re-render the rest. So no `plan` prop, and every store subscription below
 * selects a value that only changes when it matters to *this* table.
 */
interface Props {
  table: Table
  guestById: ReadonlyMap<string, Guest>
  seatLabels: SeatLabelMode
  containerRef: React.RefObject<HTMLDivElement>
}

interface DragSession {
  pointerId: number
  /** How far this pointer may wander and still be a tap. */
  slop: number
  startX: number
  startY: number
  /** The tables this gesture moves; null until known — see `onPointerDown`. */
  origins: Map<string, { x: number; y: number }> | null
  additive: boolean
  moved: boolean
}

interface RotateSession {
  pointerId: number
  centerX: number
  centerY: number
  startAngle: number
  startRotation: number
  /** Set on the first real movement — a plain click must not become an edit. */
  moved: boolean
}

const DRAG_THRESHOLD_PX = 3
/** A fingertip wobbles further than a mouse before it means to move anything. */
const TOUCH_DRAG_THRESHOLD_PX = 8

const SPRING = { type: 'spring' as const, stiffness: 420, damping: 34, mass: 0.7 }
const INSTANT = { duration: 0 }

/** Chair labels become noise below this zoom; the PDF carries the full names. */
const LABEL_ZOOM = 0.55

export const TableNode = memo(TableNodeImpl)

function TableNodeImpl({ table, guestById, seatLabels, containerRef }: Props) {
  const t = useT()
  const dragRef = useRef<DragSession | null>(null)
  const rotateRef = useRef<RotateSession | null>(null)
  const reduceMotion = useReducedMotion()

  const isSelected = usePlanStore((s) => s.selectedTableIds.includes(table.id))
  const isSwapSource = useViewStore(
    (s) => s.placing?.kind === 'swap' && s.placing.tableId === table.id,
  )
  // The toolbar belongs to *the* selection, not to each of several: with two
  // tables picked, three floating pills would argue about which one you meant.
  const isSoleSelection = usePlanStore(
    (s) => s.selectedTableIds.length === 1 && s.selectedTableIds[0] === table.id,
  )
  // Only a selected table draws anything counter-scaled; the rest need not
  // re-render on every wheel step.
  const zoom = useViewStore((s) => (isSelected ? s.zoom : 1))
  // How far out the toolbar keeps its size. A phone spends its time much further
  // out than a desktop, and a toolbar shrunk with the room is a row of specks.
  const toolbarFloor = useViewStore((s) => (isSelected && s.compact ? 0.16 : 0.35))
  const showLabels = useViewStore((s) => s.zoom >= LABEL_ZOOM)

  const swatch = screenSwatchFor(table.color)
  const geometry = computeTableGeometry(table.shape, table.seatCount)
  // Chairs turn with the table — the same rotated positions the PDF prints and
  // the clamp/extent maths assume. Centred on 0,0 because the node itself is
  // already translated to the table's centre. Memoised so each chair's `point`
  // keeps its identity and the chair can skip re-rendering.
  const seatPoints = useMemo(
    () => seatWorldPositions(table.shape, table.seatCount, 0, 0, table.rotation),
    [table.shape, table.seatCount, table.rotation],
  )

  // --- dragging the table ----------------------------------------------------

  /** Select as the press means it, and note where everything that will move starts. */
  const pickUp = (additive: boolean) => {
    const store = usePlanStore.getState()
    let moving = store.selectedTableIds

    if (additive) {
      store.toggleTableSelection(table.id)
      moving = usePlanStore.getState().selectedTableIds
    } else if (!moving.includes(table.id)) {
      store.selectTables([table.id])
      moving = [table.id]
    }

    store.bringToFront(table.id)

    const origins = new Map<string, { x: number; y: number }>()
    const current = usePlanStore.getState().plan
    for (const id of moving) {
      const entity = current?.tables.find((t) => t.id === id)
      if (entity) origins.set(id, { x: entity.x, y: entity.y })
    }
    return origins
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.stopPropagation()

    const view = useViewStore.getState()
    // The second finger of a pinch belongs to the camera.
    if (view.pinching) return
    // Select mode is Shift for a touch screen.
    const additive = event.shiftKey || event.metaKey || event.ctrlKey || view.selectMode

    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      slop: event.pointerType === 'mouse' ? DRAG_THRESHOLD_PX : TOUCH_DRAG_THRESHOLD_PX,
      startX: event.clientX,
      startY: event.clientY,
      // A mouse selects on the press. A finger waits to see what it is doing:
      // it may be the first finger of a pinch, which must select nothing, and
      // while a guest waits for a chair a tap on a table answers that instead.
      origins: view.placing || event.pointerType !== 'mouse' ? null : pickUp(additive),
      additive,
      moved: false,
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = dragRef.current
    if (!session || session.pointerId !== event.pointerId) return
    if (useViewStore.getState().pinching) return

    const dxScreen = event.clientX - session.startX
    const dyScreen = event.clientY - session.startY

    if (!session.moved) {
      if (Math.hypot(dxScreen, dyScreen) < session.slop) return
      session.moved = true
      session.origins ??= pickUp(session.additive)
      // One history entry for the whole gesture, captured at the first movement
      // so an accidental click never lands on the undo stack.
      usePlanStore.getState().beginTransaction()
    }
    const origins = session.origins
    if (!origins) return

    // Screen delta -> world delta: at 50% zoom the pointer travels twice as far
    // on screen as the table does in world space.
    const currentZoom = useViewStore.getState().zoom
    const dx = dxScreen / currentZoom
    const dy = dyScreen / currentZoom
    const shouldSnap = useViewStore.getState().snapToGrid && !event.altKey

    // One call for the whole selection: the store confines every moved table
    // against a floor that excludes all of them, so two co-moving tables can
    // never extend the floor for each other and walk the pair off it.
    usePlanStore.getState().moveTables(
      [...origins].map(([id, origin]) => ({
        id,
        x: snap(origin.x + dx, shouldSnap),
        y: snap(origin.y + dy, shouldSnap),
      })),
    )
  }

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = dragRef.current
    if (!session || session.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (session.moved) {
      // Dropped on another table? Nudge the whole moved selection to the
      // nearest clean spot. Part of the same transaction as the drag.
      usePlanStore.getState().settleTables([...(session.origins?.keys() ?? [])])
      usePlanStore.getState().setSaveStatus('dirty')
    } else if (event.type === 'pointerup') {
      tapped(session)
    }
  }

  /**
   * A press that never became a drag. Seating someone? This table takes them
   * at its first free chair. Otherwise, on the compact editor, the sheet shows
   * the table — not while picking several, where a sheet per tap would bury
   * the next table to be picked.
   */
  const tapped = (session: DragSession) => {
    const view = useViewStore.getState()
    if (tapsSuppressed(view)) return
    if (view.placing) {
      finishPlacing({ tableId: table.id }, t)
      return
    }
    if (!session.origins) pickUp(session.additive)
    if (view.compact && !view.selectMode) view.openPanel('selection')
  }

  // --- rotation handle -------------------------------------------------------

  const onRotateStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return

    const camera = useViewStore.getState()
    const centerX = rect.left + camera.x + table.x * camera.zoom
    const centerY = rect.top + camera.y + table.y * camera.zoom

    event.currentTarget.setPointerCapture(event.pointerId)
    rotateRef.current = {
      pointerId: event.pointerId,
      centerX,
      centerY,
      startAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX),
      startRotation: table.rotation,
      moved: false,
    }
  }

  const onRotateMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = rotateRef.current
    if (!session || session.pointerId !== event.pointerId) return
    if (useViewStore.getState().pinching) return
    if (!session.moved) {
      session.moved = true
      // One history entry for the whole turn, opened only once the handle has
      // actually moved — pressing it and letting go must not touch the stack.
      usePlanStore.getState().beginTransaction()
    }
    const angle = Math.atan2(event.clientY - session.centerY, event.clientX - session.centerX)
    let degrees = session.startRotation + ((angle - session.startAngle) * 180) / Math.PI
    // Shift frees the rotation; otherwise snap to 15° so tables stay tidy.
    if (!event.shiftKey) degrees = Math.round(degrees / 15) * 15
    usePlanStore.getState().setTableRotation(table.id, degrees)
  }

  const onRotateEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    const session = rotateRef.current
    if (!session || session.pointerId !== event.pointerId) return
    rotateRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (session.moved) {
      // Turning a table can swing its long side into a neighbour just as surely
      // as dragging can; a rotation settles the same way a drop does. A plain
      // click on the handle settles nothing — an untouched table never moves.
      usePlanStore.getState().settleTables([table.id])
      usePlanStore.getState().setSaveStatus('dirty')
    }
  }

  // --- render ----------------------------------------------------------------

  const occupied = table.seats.filter((seat) => seat.guestId).length
  const spring = reduceMotion ? INSTANT : SPRING

  /*
   * While this table is being dragged or turned and would come to rest on a
   * neighbour, its top warns in red — the promise that releasing here will
   * nudge it aside, made *before* the release. Only tables outside the moving
   * selection count: a multi-select moves as one piece of furniture, and the
   * release settles it against the rest of the room the same way.
   */
  const gestureLive = Boolean(dragRef.current?.moved || rotateRef.current)
  // Read, not subscribed: only a table mid-gesture asks, and it re-renders on
  // every step of that gesture anyway.
  const { plan: livePlan, selectedTableIds } = usePlanStore.getState()
  const wouldCollide =
    gestureLive &&
    (livePlan?.tables ?? []).some(
      (other) =>
        other.id !== table.id &&
        !selectedTableIds.includes(other.id) &&
        tablesCollide(table, other),
    )

  /*
   * How far above the table's centre the toolbar floats. It has to clear the
   * chairs *and* whatever they are drawn as — a name plate reaches further out
   * than a disc does, and a pill sitting on top of the top-most plate is the
   * one thing that makes the toolbar look like a bug rather than a control.
   * `halfExtent` deliberately excludes the plate (the collision solver depends
   * on that), so the reach is added here rather than taken from it.
   */
  const plateReach = seatLabels === 'name' ? SEAT_PLATE_REACH : 0
  const toolbarOffset = geometry.height / 2 + SEAT_RADIUS * 2 + plateReach + 40

  return (
    <motion.div
      className={`table-node${isSelected ? ' is-selected' : ''}${isSwapSource ? ' is-swap-source' : ''}`}
      style={{ left: table.x, top: table.y, zIndex: table.zIndex }}
      initial={reduceMotion ? false : { opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={spring}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      data-table-id={table.id}
    >
      {/* Table top. Rotates with the table; the name does not, so it stays readable. */}
      <motion.div
        className={`table-top table-top--${table.shape}${wouldCollide ? ' table-top--colliding' : ''}`}
        animate={{
          width: geometry.width,
          height: geometry.height,
          rotate: table.rotation,
        }}
        transition={spring}
        style={{
          background: swatch.surface,
          borderColor: isSelected ? swatch.accent : swatch.edge,
          marginLeft: -geometry.width / 2,
          marginTop: -geometry.height / 2,
        }}
      />

      <div className="table-label" style={{ color: swatch.ink }}>
        <span className="table-label-name">{table.name}</span>
        <span className="table-label-count">
          {occupied}/{table.seatCount}
        </span>
      </div>

      {seatPoints.map((point) => {
        const seat = table.seats[point.index]
        if (!seat) return null
        return (
          <SeatNode
            key={seat.id}
            seat={seat}
            point={point}
            table={table}
            guest={seat.guestId ? (guestById.get(seat.guestId) ?? null) : null}
            swatch={swatch}
            showLabel={showLabels}
            labels={seatLabels}
            spring={spring}
          />
        )
      })}

      {/* The controls that belong to *this* table travel with it, rather than
          living in a panel on the far side of the screen. Counter-scaled so the
          pill stays a readable UI size at any zoom, and stopPropagation'd so a
          press on it never starts a drag of the table underneath. */}
      {isSoleSelection && (
        /*
         * Positioning and animation are split across two elements on purpose:
         * framer-motion owns the `transform` of anything it animates, so the
         * counter-scale had to live on a plain wrapper or it would be silently
         * overwritten the moment the enter animation ran (which centred the
         * pill on the table's left edge rather than on the table).
         */
        <div
          className="table-toolbar-anchor"
          style={{
            top: -toolbarOffset,
            transform: `translateX(-50%) scale(${1 / Math.max(zoom, toolbarFloor)})`,
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
        <motion.div
          className="table-toolbar"
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.14, ease: [0.23, 1, 0.32, 1] }}
        >
          <span className="mono table-toolbar-stat">
            {t.canvas.chairsSeated(table.seatCount, occupied)}
          </span>
          <span className="table-toolbar-rule" />
          <button
            type="button"
            className="table-toolbar-btn table-toolbar-btn--grab"
            onPointerDown={onRotateStart}
            onPointerMove={onRotateMove}
            onPointerUp={onRotateEnd}
            onPointerCancel={onRotateEnd}
            title={t.canvas.rotateHint}
            aria-label={t.canvas.rotateTable(table.name)}
          >
            {t.canvas.rotate}
          </button>
          <button
            type="button"
            className="table-toolbar-btn"
            onClick={() => usePlanStore.getState().duplicateTable(table.id)}
          >
            {t.common.duplicate}
          </button>
          <button
            type="button"
            className="table-toolbar-btn table-toolbar-btn--danger"
            onClick={() => usePlanStore.getState().deleteTables([table.id])}
          >
            {t.common.remove}
          </button>
        </motion.div>
        </div>
      )}

      {isSelected && useViewStore.getState().snapToGrid && dragRef.current?.moved && (
        <span className="table-snap-hint" style={{ transform: `scale(${1 / zoom})` }}>
          {Math.round(table.x)}, {Math.round(table.y)}
        </span>
      )}
    </motion.div>
  )
}
